import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  impact,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  footerLeft,
  contentClassName,
  dangerous,
  onConfirm,
  busy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  impact?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  footerLeft?: ReactNode;
  contentClassName?: string;
  dangerous?: boolean;
  onConfirm: () => void | Promise<void>;
  busy?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] max-h-[min(90dvh,40rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto",
            "rounded-rs border border-rs-border bg-rs-elevated p-5 shadow-rs",
            contentClassName,
          )}
        >
          <Dialog.Title className="text-base font-semibold text-rs-ink">
            {title}
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-rs-ink-muted">
            {description}
          </Dialog.Description>
          {impact ? (
            <div className="mt-3 rounded-rs-sm border border-rs-border bg-rs-muted px-3 py-2 text-sm text-rs-ink">
              {impact}
            </div>
          ) : null}
          <div
            className={cn(
              "mt-5 flex gap-2",
              footerLeft ? "items-center justify-between" : "justify-end",
            )}
          >
            {footerLeft ? (
              <div className="min-w-0 shrink">{footerLeft}</div>
            ) : null}
            <div className="flex shrink-0 justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                {cancelLabel}
              </Button>
              <Button
                variant={dangerous ? "danger" : "primary"}
                disabled={busy}
                onClick={() => void onConfirm()}
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
